import type { CrawlerShareMeta } from './crawlerShareMeta.js';

type HubPageMeta = Omit<CrawlerShareMeta, 'image'> & { image: string };

/** Legacy T&B hub page SEO — substituted at edge when tenant contact is provided. */
export const CRAWLER_HUB_PAGE_META: Record<string, HubPageMeta> = {
  '/our-story': {
    title: "About Tools & Bubbles | Kochi's #1 Professional Cleaning Service Since 2009",
    description:
      "Tools & Bubbles is Kerala's leading cleaning service, founded in 2009. 15+ years experience, 10,000+ customers, 4.8★ Google rating, 50+ trained professionals. Kärcher equipment, 3M chemicals, fully insured.",
    image: '/icon-512.png',
  },
  '/faq': {
    title: 'Cleaning Service FAQ Kochi | Tools & Bubbles | Pricing, Booking & Coverage',
    description:
      'Answers to every question about cleaning services in Kochi — pricing, service areas, deep cleaning duration, AMC plans, insurance, same-day booking and more. 4.8★ Google rated cleaning company serving Kochi since 2009.',
    image: '/icon-512.png',
  },
  '/testimonials': {
    title: 'Customer Reviews & Testimonials | Tools & Bubbles Kochi | 4.8★ Google Rated',
    description:
      "Read 230+ verified 5-star reviews for Tools & Bubbles — Kochi's most trusted professional cleaning service since 2009. Real stories from Kakkanad, Edappally, Kaloor, Palarivattom and across Ernakulam.",
    image: '/icon-512.png',
  },
  '/franchise': {
    title: 'Cleaning Service Franchise | Tools & Bubbles | Partner With Us',
    description:
      "Partner with Tools & Bubbles — Kerala's #1 cleaning brand. Franchise opportunities with proven SOPs, training, marketing support, and recurring revenue. Enquire about your city.",
    image: '/icon-512.png',
  },
  '/contact-us': {
    title: 'Contact Tools & Bubbles | Call, WhatsApp or Email',
    description:
      'Reach Tools & Bubbles — call, WhatsApp, email, or visit us in Kochi. Same-day cleaning service across Ernakulam.',
    image: '/icon-512.png',
  },
  '/careers': {
    title: 'Careers at Tools & Bubbles | Jobs in Kochi | Driver, Cleaners, Housekeeping Staff',
    description:
      "Join Tools & Bubbles - Kochi's #1 cleaning company! We are hiring Supervisors, Drivers, Professional Cleaners, Housekeeping Staff & Maids. Great salary, benefits & growth!",
    image: '/icon-512.png',
  },
  '/blog': {
    title: 'Cleaning Tips & Guides | Tools & Bubbles Blog | Deep Cleaning Kochi',
    description:
      "Expert cleaning tips, guides & local advice for Kochi homes. Learn about deep cleaning, sofa cleaning, bathroom cleaning, and home care from Tools & Bubbles - Kochi's #1 cleaning service with 15+ years experience.",
    image: '/icon-512.png',
  },
  '/insurance': {
    title: 'Insurance Coverage | Workmen Compensation & Public Liability | Tools & Bubbles Kochi',
    description:
      'Tools & Bubbles is fully insured with Workmen Compensation Insurance and Public Liability Insurance. Protecting both our cleaning staff and your property in Kochi, Ernakulam. Book with confidence!',
    image: '/icon-512.png',
  },
  '/amc-services': {
    title: 'AMC Cleaning Services | Annual Maintenance Contract | Up to 25% Discount | Tools & Bubbles',
    description:
      'Subscribe to our AMC cleaning services and save up to 25%! Regular home & office cleaning with priority scheduling, dedicated teams, and quality guarantee. Monthly, quarterly & half-yearly plans available.',
    image: '/icon-512.png',
  },
  '/property-management': {
    title: 'Property Management Services | NRI Home Care | Tools & Bubbles',
    description:
      'End-to-end property management and caretaker services from Tools & Bubbles. Monthly upkeep, deep cleaning, monsoon checks, and vendor coordination in Kochi.',
    image: '/icon-512.png',
  },
  '/cleaning-chemicals': {
    title: '3M Cleaning Chemicals & Kärcher Equipment | MSDS | Tools & Bubbles Kochi',
    description:
      'Tools & Bubbles uses premium 3M professional cleaning chemicals and Kärcher equipment. View MSDS & data sheets. Safe, eco-friendly cleaning in Kochi!',
    image: '/icon-512.png',
  },
  '/our-staff': {
    title: 'Our Staff | Trained Cleaning Team in Kochi | Tools & Bubbles',
    description:
      'Meet Tools & Bubbles trained cleaning staff in Kochi. Learn about our quality standards, safety process, and customer-first team culture.',
    image: '/icon-512.png',
  },
  '/sustainability': {
    title: 'Sustainability & Green Cleaning | 3M & Kärcher Practices | Tools & Bubbles Kochi',
    description:
      'Learn how Tools & Bubbles runs a responsible cleaning program in Kochi: correct dilution, MSDS-safe chemistry, waste-aware workflows, and alignment with 3M and Kärcher sustainability principles.',
    image: '/icon-512.png',
  },
  '/terms': {
    title: 'Terms & Conditions | Tools & Bubbles Cleaning Services',
    description:
      'Read Tools & Bubbles terms and conditions for booking professional cleaning services in Kochi — cancellation, rescheduling, payment, quality standards, and liability.',
    image: '/icon-512.png',
  },
  '/privacy-policy': {
    title: 'Privacy Policy | Tools & Bubbles Cleaning Services',
    description:
      'Tools & Bubbles privacy policy — how we collect, use, and protect your personal information when you book cleaning services or use our website.',
    image: '/icon-512.png',
  },
  '/search': {
    title: 'Search Cleaning Services Kochi | Tools & Bubbles',
    description:
      'Search Tools & Bubbles service and location pages — deep cleaning, sofa cleaning, office cleaning, and neighbourhoods across Greater Kochi. Then book online.',
    image: '/images/hero-deep-cleaning-kochi.png',
  },
  '/addon-services': {
    title: 'Special Services in Kochi | Sofa, Kitchen, Pest Control, Water Tank | Tools & Bubbles',
    description:
      'Special services and add-ons in Kochi with transparent pricing. Sofa cleaning, mattress cleaning, kitchen cleaning, water tank cleaning, pest control, AC duct cleaning in Kakkanad, Kaloor, Edappally, Ernakulam. Guides and online booking.',
    image: '/images/addon-sofa-cleaning.jpg',
  },
};

/** Map alternate paths to canonical hub meta keys. */
export const CRAWLER_HUB_PATH_ALIASES: Record<string, string> = {
  '/about-us': '/our-story',
  '/team': '/our-staff',
  '/chemicals': '/cleaning-chemicals',
  '/equipment': '/cleaning-chemicals',
  '/amc': '/amc-services',
  '/annual-maintenance': '/amc-services',
  '/amc-cleaning': '/amc-services',
  '/privacy': '/privacy-policy',
  '/green-cleaning': '/sustainability',
  '/eco-cleaning': '/sustainability',
  '/addons': '/addon-services',
  '/add-ons': '/addon-services',
};

export function resolveCrawlerHubPageMeta(path: string): HubPageMeta | null {
  const canonical = CRAWLER_HUB_PATH_ALIASES[path] ?? path;
  return CRAWLER_HUB_PAGE_META[canonical] ?? null;
}
